import type { VariantProps } from "class-variance-authority"
import type { ComponentPropsWithoutRef } from "react"
import { forwardRef, memo, useCallback, useSyncExternalStore } from "react"
import { cva } from "class-variance-authority"

import type { ShortcutValue } from "@productlane/lib/src/shortcut-symbols"
import { cn } from "@productlane/lib/src/browser"
import { ShortcutValueToKeyToken } from "@productlane/lib/src/shortcut-symbols"

export const shortcutVariants = cva(
	"flex flex-none m-px items-center justify-center rounded-md px-1 text-[11px] font-medium uppercase min-w-[1.5em] tracking-wide transition-colors duration-100",
	{
		variants: {
			variant: {
				default:
					"bg-white text-tertiary ring-1 ring-inset ring-gray-200 dark:bg-gray-900 dark:ring-gray-700",
				ghost:
					"ring-transparent bg-transparent text-tertiary dark:text-tertiary shadow-none px-0 ring-none min-w-[10px]",
				portal:
					"bg-[var(--portal-accent-color)] text-[var(--portal-button-text-color)]",
			},
			pressed: {
				true: "",
				false: "",
			},
		},
		compoundVariants: [
			{
				variant: "default",
				pressed: true,
				className:
					"bg-gray-200 text-gray-900 ring-gray-300 dark:bg-gray-700 dark:text-white dark:ring-gray-600",
			},
			{
				variant: "portal",
				pressed: true,
				className: "brightness-90",
			},
		],
		defaultVariants: {
			variant: "default",
			pressed: false,
		},
	},
)

export type ShortcutVariant = VariantProps<typeof shortcutVariants>["variant"]

const heldTokens = new Set<string>()
const listeners = new Set<() => void>()
let attached = false

function emit() {
	for (const listener of listeners) listener()
}

function toggle(token: string, active: boolean) {
	if (active) heldTokens.add(token)
	else heldTokens.delete(token)
}

function syncModifiers(e: KeyboardEvent) {
	toggle("Meta", e.metaKey)
	toggle("Shift", e.shiftKey)
	toggle("Alt", e.altKey)
	toggle("Control", e.ctrlKey)
}

function attach() {
	if (attached || typeof window === "undefined") return
	attached = true
	const onDown = (e: KeyboardEvent) => {
		syncModifiers(e)
		heldTokens.add(e.code)
		emit()
	}
	const onUp = (e: KeyboardEvent) => {
		syncModifiers(e)
		heldTokens.delete(e.code)
		emit()
	}
	const clear = () => {
		if (!heldTokens.size) return
		heldTokens.clear()
		emit()
	}
	window.addEventListener("keydown", onDown, true)
	window.addEventListener("keyup", onUp, true)
	window.addEventListener("blur", clear)
	document.addEventListener("visibilitychange", () => {
		if (document.hidden) clear()
	})
}

function useHeldKey(symbol?: ShortcutValue) {
	const token = symbol ? ShortcutValueToKeyToken[symbol] : undefined
	const subscribe = useCallback(
		(onChange: () => void) => {
			if (!token) return () => undefined
			attach()
			listeners.add(onChange)
			return () => {
				listeners.delete(onChange)
			}
		},
		[token],
	)
	const getSnapshot = useCallback(
		() => (token ? heldTokens.has(token) : false),
		[token],
	)
	return useSyncExternalStore(subscribe, getSnapshot, () => false)
}

interface ShortcutProps extends ComponentPropsWithoutRef<"span"> {
	variant?: ShortcutVariant
	// Overrides the glyph used for live press tracking; inferred from children otherwise.
	liveSymbol?: ShortcutValue
	// Set false to render a static pill that never darkens on key press.
	live?: boolean
}

function toShortcutValue(value: unknown): ShortcutValue | undefined {
	if (typeof value !== "string") return undefined
	if (value in ShortcutValueToKeyToken) return value as ShortcutValue
	const upper = value.toUpperCase()
	if (upper in ShortcutValueToKeyToken) return upper as ShortcutValue
	return undefined
}

export const Shortcut = memo(
	forwardRef<HTMLSpanElement, ShortcutProps>(
		(
			{
				className,
				variant = "default",
				liveSymbol,
				live = true,
				children,
				...props
			},
			ref,
		) => {
			const symbol = liveSymbol ?? toShortcutValue(children)
			const pressed = useHeldKey(live ? symbol : undefined)
			return (
				<span
					ref={ref}
					{...props}
					className={cn(shortcutVariants({ variant, pressed }), className)}
					style={{
						height: "1.5em",
						lineHeight: "initial",
					}}
				>
					{children ?? liveSymbol}
				</span>
			)
		},
	),
)
